import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  auth,
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from '../firebase.js';
import { FormField } from '../components/FormField.jsx';

export default function Login() {
  const nav = useNavigate();
  // Stages: 'credentials' → 'mfa-challenge' → (navigate away)
  const [stage, setStage] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // MFA state
  const [resolver, setResolver] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [code, setCode] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const recaptchaRef = useRef(null);
  const verifierRef = useRef(null);

  useEffect(() => {
    if (stage !== 'mfa-challenge' || !recaptchaRef.current) return;
    if (!verifierRef.current) {
      try {
        verifierRef.current = new RecaptchaVerifier(auth, recaptchaRef.current, { size: 'invisible' });
      } catch (e) {
        setErr(`Verifier init failed: ${e.message}`);
      }
    }
    return () => {
      if (verifierRef.current) {
        try { verifierRef.current.clear(); } catch { /* noop */ }
        verifierRef.current = null;
      }
    };
  }, [stage]);

  async function sendSms(r) {
    const hint = r.hints.find((h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
    if (!hint) throw new Error('No phone factor available on this account.');
    setMaskedPhone(hint.phoneNumber || '(phone)');
    if (!verifierRef.current) throw new Error('reCAPTCHA not ready.');
    const provider = new PhoneAuthProvider(auth);
    return provider.verifyPhoneNumber(
      { multiFactorHint: hint, session: r.session },
      verifierRef.current,
    );
  }

  async function submitCredentials(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      nav('/profile');
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') {
        try {
          const r = getMultiFactorResolver(auth, e);
          setResolver(r);
          setStage('mfa-challenge');
          // sendSms needs the verifier, which is created in useEffect after
          // stage flips. Defer slightly so the ref exists.
          setTimeout(async () => {
            try {
              const vid = await sendSms(r);
              setVerificationId(vid);
            } catch (err2) {
              setErr(prettify(err2));
            }
          }, 50);
        } catch (e2) {
          setErr(prettify(e2));
        }
      } else {
        setErr(prettify(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitMfaCode(e) {
    e.preventDefault();
    setErr(null);
    if (!resolver || !verificationId) { setErr('Missing MFA state. Start over.'); return; }
    if (!/^\d{6}$/.test(code)) { setErr('Enter the 6-digit code.'); return; }
    setBusy(true);
    try {
      const cred = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await resolver.resolveSignIn(assertion);
      nav('/profile');
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setBusy(false);
    }
  }

  async function resendSms() {
    setErr(null); setBusy(true);
    try {
      const vid = await sendSms(resolver);
      setVerificationId(vid);
      setCode('');
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center">
      <div className="max-w-sm w-full mx-auto px-4">
        <div className="eyebrow text-brand mb-3">Members</div>
        <h1 className="font-display text-4xl mb-8">
          {stage === 'credentials' ? 'Sign in.' : 'Enter your code.'}
        </h1>

        {stage === 'credentials' && (
          <>
            <form onSubmit={submitCredentials} className="space-y-4">
              <FormField label="Email">
                <input
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </FormField>
              <FormField label="Password">
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormField>
              {err && <p className="text-sm text-danger">{err}</p>}
              <button type="submit" className="btn-primary w-full h-11" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            <p className="text-sm text-ink-2 mt-8">
              Not a member yet?{' '}
              <Link to="/signup" className="text-brand underline underline-offset-4 font-medium">
                Create an account
              </Link>
            </p>
          </>
        )}

        {stage === 'mfa-challenge' && (
          <>
            <p className="text-sm text-ink-2 mb-6">
              We texted a 6-digit code to <span className="font-mono">{maskedPhone}</span>.
            </p>
            <form onSubmit={submitMfaCode} className="space-y-4">
              <FormField label="Code">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  className="input font-mono text-center tracking-[0.3em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  required
                />
              </FormField>
              {err && <p className="text-sm text-danger">{err}</p>}
              <button type="submit" className="btn-primary w-full h-11" disabled={busy}>
                {busy ? 'Verifying…' : 'Verify'}
              </button>
            </form>
            <div className="mt-4 flex items-center justify-between text-xs">
              <button type="button" className="text-brand underline underline-offset-2" onClick={resendSms} disabled={busy}>
                Resend code
              </button>
              <button
                type="button"
                className="text-ink-3 underline underline-offset-2"
                onClick={() => { setStage('credentials'); setResolver(null); setVerificationId(null); setCode(''); setErr(null); }}
              >
                Use a different account
              </button>
            </div>
          </>
        )}
        <div ref={recaptchaRef} className="hidden" />
      </div>
    </div>
  );
}

function prettify(e) {
  const code = e?.code || '';
  const map = {
    'auth/invalid-credential': 'Email or password is incorrect.',
    'auth/user-not-found': 'No account with that email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/too-many-requests': 'Too many attempts. Try again in a minute.',
    'auth/invalid-verification-code': 'That code is incorrect. Try again.',
    'auth/code-expired': 'Code expired. Request a new one.',
    'auth/missing-verification-code': 'Enter the 6-digit code from the SMS.',
    'auth/user-disabled': 'This account is disabled.',
  };
  return map[code] || e?.message || 'Sign-in failed.';
}
